import type { LngLat } from 'mapbox-gl';

import { ResultsFieldPopupData } from '../components/results-field-popup';
import { parseYearReference, parseNumeric } from '../utils/format-metrics';
import { resolveFeatureMapColor } from '../utils/results-map-legend';
import { getGesStatusCssColor } from '../utils/ges-status';

function parseJsonObject<T extends Record<string, unknown>>(
  value: unknown,
): T | null {
  if (typeof value === 'object' && value !== null) {
    return value as T;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as T;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function parseFarm(
  props: Record<string, unknown>,
): ResultsFieldPopupData['farm'] {
  const farm = parseJsonObject<{ id: number; name: string }>(props.farm);
  return farm && typeof farm.name === 'string' ? farm : null;
}

function parseCampaign(
  props: Record<string, unknown>,
): ResultsFieldPopupData['campaign'] {
  const campaign = parseJsonObject<{
    id: number;
    name: string;
    year?: number;
  }>(props.campaign);
  return campaign && typeof campaign.name === 'string' ? campaign : null;
}

export function parseResultsMapFeaturePopup(
  props: Record<string, unknown>,
  lngLat: LngLat,
): ResultsFieldPopupData {
  const gesStatus = parseNumeric(props.ges_status);

  return {
    longitude: lngLat.lng,
    latitude: lngLat.lat,
    name: (props.name as string) ?? null,
    color:
      resolveFeatureMapColor('ges_status', props) ??
      getGesStatusCssColor(gesStatus) ??
      ((props.color as string) ?? null),
    code: (props.code as string) ?? null,
    farm: parseFarm(props),
    campaign: parseCampaign(props),
    ges_status: gesStatus,
    csr_year_reference: parseYearReference(props.csr_year_reference),
    csa_year_reference: parseYearReference(props.csa_year_reference),
    csr_carbon_content: parseNumeric(props.csr_carbon_content),
    csr_soil_density: parseNumeric(props.csr_soil_density),
    csr_carbon_stock: parseNumeric(props.csr_carbon_stock),
    csr_lower_carbon_stock_threshold: parseNumeric(
      props.csr_lower_carbon_stock_threshold,
    ),
    csr_upper_carbon_stock_threshold: parseNumeric(
      props.csr_upper_carbon_stock_threshold,
    ),
    csr_median_carbon_stock: parseNumeric(props.csr_median_carbon_stock),
    csa_carbon_content: parseNumeric(props.csa_carbon_content),
    csa_soil_density: parseNumeric(props.csa_soil_density),
    csa_carbon_stock: parseNumeric(props.csa_carbon_stock),
    csa_lower_carbon_stock_threshold: parseNumeric(
      props.csa_lower_carbon_stock_threshold,
    ),
    csa_upper_carbon_stock_threshold: parseNumeric(
      props.csa_upper_carbon_stock_threshold,
    ),
    csa_median_carbon_stock: parseNumeric(props.csa_median_carbon_stock),
  };
}
